import { useEffect, useState } from "react";
import {
  useEmailSignatureQuery,
  useUpdateEmailSignatureMutation,
  useUploadSignatureImageMutation,
} from "../api/queries";
import {
  Card,
  Text,
  Button,
  Loader,
  Group,
  Stack,
  Textarea,
  Switch,
  FileButton,
} from "@mantine/core";
import { toast } from "sonner";

export function EmailSignatureSettings() {
  const { data: signature, isLoading } = useEmailSignatureQuery();
  const { mutate: updateSignature, isPending: isUpdating } =
    useUpdateEmailSignatureMutation();
  const { mutate: uploadImage, isPending: isUploading } =
    useUploadSignatureImageMutation();

  const [text, setText] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (signature) {
      const displayText = (signature.text || "").replace(/<br\s*\/?>/gi, "\n");
      setText(displayText);
      setEnabled(signature.enabled);
      setImageUrl(signature.image_url);
      if (signature.image_base64) {
        setImagePreview(signature.image_base64);
      } else {
        setImagePreview(null);
      }
    }
  }, [signature]);

  const handleFileChange = async (file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image file size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    uploadImage(file, {
      onSuccess: (data) => {
        setImageUrl(data.image_url);
        toast.success("Image uploaded successfully");
      },
      onError: (err) => {
        toast.error("Failed to upload image");
        console.error(err);
      },
    });
  };

  const handleSave = () => {
    const htmlText = text ? text.replace(/\n/g, "<br/>") : null;

    updateSignature(
      {
        text: htmlText,
        image_url: imageUrl,
        enabled,
      },
      {
        onSuccess: () => {
          toast.success("Email signature saved successfully");
        },
        onError: (err) => {
          toast.error("Failed to save email signature");
          console.error(err);
        },
      },
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Text fw={500} c="dimmed">
            Email Signature
          </Text>
          <Text size="sm" c="gray">
            Add a signature that will be automatically appended to your emails.
          </Text>
        </div>
        <Switch
          label="Enable signature"
          checked={enabled}
          onChange={(e) => setEnabled(e.currentTarget.checked)}
          disabled={isUpdating}
        />
      </Group>

      <FileButton
        onChange={handleFileChange}
        accept="image/*"
        disabled={isUploading}
      >
        {(props) => (
          <Button
            {...props}
            variant="outline"
            loading={isUploading}
            disabled={isUploading}
          >
            {imageUrl || imagePreview ? "Change Image" : "Upload Image"}
          </Button>
        )}
      </FileButton>

      <Textarea
        label="Signature Text"
        placeholder="Enter your signature text (supports HTML)"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        minRows={4}
        disabled={isUpdating}
      />

      <div>
        <Text size="sm" c="gray" mb="xs">
          Preview:
        </Text>
        <Card withBorder p="md" style={{ minHeight: "100px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Signature"
                style={{
                  width: "100px",
                  height: "100px",
                  objectFit: "contain",
                }}
              />
            ) : null}
            {text ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: text.replace(/\n/g, "<br/>"),
                }}
              />
            ) : null}
          </div>
        </Card>
      </div>

      <Group justify="flex-end">
        <Button onClick={handleSave} loading={isUpdating} disabled={isUpdating}>
          Save Signature
        </Button>
      </Group>
    </Stack>
  );
}
